import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Tabs,
  Tab,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import axios from "axios";

const ParsedDataContainer = styled(Box)({
  backgroundColor: "rgba(28, 28, 30, 0.95)",
  padding: "20px",
  borderRadius: "8px",
  maxHeight: "80vh",
  overflowY: "auto",
  color: "white",
});

const JsonEditor = styled(TextField)({
  width: "100%",
  "& .MuiInputBase-root": {
    color: "white",
    fontFamily: "monospace",
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "rgba(255, 255, 255, 0.23)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(255, 255, 255, 0.4)",
    },
  },
});

// New styled component for displaying JSON data
const JsonDisplay = styled(Box)({
  whiteSpace: "pre-wrap", // Allows text to wrap and preserves whitespace
  wordBreak: "break-all", // Breaks words at any character to prevent overflow
  fontFamily: "monospace",
  backgroundColor: "rgba(0, 0, 0, 0.2)", // Slightly dark background for the JSON
  padding: "10px",
  borderRadius: "4px",
  maxHeight: "60vh", // Max height before scrolling
  overflowY: "auto", // Enable vertical scrolling
  color: "rgba(255, 255, 255, 0.87)", // Light text color for readability
});

const MessageBubble = styled(Box)(({ isUser }) => ({
  backgroundColor: isUser ? "#0084ff" : "#3a3b3c",
  color: "white",
  padding: "8px 12px",
  borderRadius: "18px",
  maxWidth: "80%",
  marginBottom: "8px",
  alignSelf: isUser ? "flex-end" : "flex-start",
  whiteSpace: "pre-wrap",
}));

// New Helper component to render object/array data in a human-readable format
const HumanReadableContent = ({ data, indentLevel = 0 }) => {
  const baseIndentSpacing = 3; // Increased base indent unit (e.g., from 2 to 3)

  if (data === null || data === undefined) {
    return <Typography variant="body1" sx={{ color: "rgba(255, 255, 255, 0.7)", ml: indentLevel * baseIndentSpacing, fontStyle: "italic" }}>N/A</Typography>;
  }

  if (Array.isArray(data)) {
    if (data.length === 0) {
      return <Typography variant="body1" sx={{ color: "rgba(255, 255, 255, 0.7)", ml: indentLevel * baseIndentSpacing, fontStyle: "italic" }}>[] (empty)</Typography>;
    }
    return (
      <Box component="ul" sx={{ pl: indentLevel * baseIndentSpacing + 2, m: 0, listStyleType: "disc" }}>
        {data.map((item, index) => (
          <Box component="li" key={index} sx={{ mb: 1.5 }}> {/* Increased li bottom margin */}
            <HumanReadableContent data={item} indentLevel={indentLevel + 1} />
          </Box>
        ))}
      </Box>
    );
  }

  if (typeof data === 'object') {
    const entries = Object.entries(data);
    if (entries.length === 0) {
      return <Typography variant="body1" sx={{ color: "rgba(255, 255, 255, 0.7)", ml: indentLevel * baseIndentSpacing, fontStyle: "italic" }}>{} (empty)</Typography>;
    }
    return (
      <Box sx={{ ml: indentLevel * baseIndentSpacing }}>
        {entries.map(([key, value]) => (
          <Box key={key} sx={{ mb: 1.5 }}> {/* Increased key-value pair bottom margin */}
            <Typography component="span" variant="subtitle1" sx={{ fontWeight: 'bold', color: "rgba(255, 255, 255, 0.95)" }}> {/* Changed variant to subtitle1 */}
              {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
            </Typography>
            {(typeof value === 'object' && value !== null) ? (
              <Box sx={{mt: 0.5}}> {/* Add a little top margin for nested objects/arrays */}
                <HumanReadableContent data={value} indentLevel={indentLevel + 1} />
              </Box>
            ) : (
              <Typography component="span" variant="body1" sx={{ color: "rgba(255, 255, 255, 0.87)", ml: 1.5 }}> {/* Changed variant to body1, increased ml */}
                {String(value)}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    );
  }

  // For primitives (string, number, boolean)
  return <Typography variant="body1" sx={{ color: "rgba(255, 255, 255, 0.87)", display: 'inline', ml: indentLevel * baseIndentSpacing  }}>{String(data)}</Typography>; {/* Changed variant to body1 */}
};

const ParsedDataDisplay = ({ parsedData, exampleId, exampleMessages }) => {
  const [isEditingTranscript, setIsEditingTranscript] = useState(false);
  const [transcriptJson, setTranscriptJson] = useState("");
  const [editError, setEditError] = useState("");
  // const [currentTab, setCurrentTab] = useState(
  //   exampleMessages?.length > 0 ? 0 : 1
  // ); // Old tab state removed

  // Dynamically build tab configuration based on available data
  const tabConfig = React.useMemo(() => {
    const TABS = [];
    if (exampleMessages && exampleMessages.length > 0) {
      TABS.push({ key: "messages", label: "User Follow Up Messages" });
    }
    if (parsedData?.transcript?.length > 0) {
      TABS.push({ key: "transcript", label: "Transcript" });
    }
    if (
      parsedData?.dating_profile_details ||
      parsedData?.observations
    ) {
      TABS.push({ key: "imageParsedData", label: "Image Details" });
    }
    return TABS;
  }, [exampleMessages, parsedData]);

  const [activeTabIndex, setActiveTabIndex] = useState(0); // Index of the currently active tab

  React.useEffect(() => {
    // If no tabs are available, set active index to -1
    if (tabConfig.length === 0) {
      setActiveTabIndex(-1);
      return;
    }

    // Try to find and set "Transcript" as the default active tab
    const transcriptIndex = tabConfig.findIndex(tab => tab.key === 'transcript');
    if (transcriptIndex !== -1) {
      setActiveTabIndex(transcriptIndex);
      return; // Exit if "Transcript" is set
    }

    // If "Transcript" is not found, try to find and set "Image Details" as the default
    const imageDetailsIndex = tabConfig.findIndex(tab => tab.key === 'imageParsedData');
    if (imageDetailsIndex !== -1) {
      setActiveTabIndex(imageDetailsIndex);
      return; // Exit if "Image Details" is set
    }

    // If neither "Transcript" nor "Image Details" are found,
    // and there are tabs available, default to the first tab in the configuration (index 0).
    // This typically would be "User Follow Up Messages" if it exists.
    setActiveTabIndex(0);

  }, [tabConfig]); // This effect should run when the tabConfig changes.

  const handleEditClick = () => {
    setTranscriptJson(JSON.stringify(parsedData.transcript, null, 2));
    setIsEditingTranscript(true);
    setEditError("");
  };

  const handleSaveTranscript = async () => {
    try {
      // Validate JSON
      const parsedJson = JSON.parse(transcriptJson);

      // Send to backend
      await axios.post(`/api/update_transcript/${exampleId}/`, {
        transcript: parsedJson,
      });

      // Update local state
      parsedData.transcript = parsedJson;
      setIsEditingTranscript(false);
      setEditError("");
    } catch (error) {
      setEditError(
        error instanceof SyntaxError
          ? "Invalid JSON format"
          : "Error saving transcript"
      );
    }
  };

  const renderTranscript = (transcript) => {
    return transcript.map((entry, index) => (
      <Box key={index} sx={{ mb: 1 }}>
        <Typography
          variant="subtitle2"
          sx={{
            color: entry.name.toLowerCase() === "me" ? "#FF3B30" : "#4CAF50",
            fontWeight: "bold",
          }}
        >
          {entry.name}:
        </Typography>
        {entry.messages.map((msg, msgIndex) => (
          <Typography
            key={msgIndex}
            variant="body2"
            sx={{
              color: "rgba(255, 255, 255, 0.87)",
              ml: 2,
            }}
          >
            {msg}
          </Typography>
        ))}
      </Box>
    ));
  };

  const renderConversation = (messages) => {
    if (!messages || !Array.isArray(messages)) return null;

    return messages.map((message, index) => {
      if (message.role === "system") return null;

      const isUser = message.role === "user";

      return (
        <Box
          key={index}
          sx={{
            display: "flex",
            flexDirection: "column",
            mb: 2,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "rgba(255, 255, 255, 0.6)",
              mb: 0.5,
              alignSelf: isUser ? "flex-end" : "flex-start",
            }}
          >
            {isUser ? "User" : "Assistant"}
          </Typography>
          <MessageBubble isUser={isUser}>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {message.content}
            </Typography>
          </MessageBubble>
        </Box>
      );
    });
  };

  // Determine the key of the current active tab
  const currentTabKey = tabConfig[activeTabIndex]?.key;

  // If no data is available for any tabs, display a message.
  if (tabConfig.length === 0) {
    return (
      <ParsedDataContainer>
        <Typography sx={{ color: "rgba(255, 255, 255, 0.7)", textAlign: "center", p: 3 }}>
          No parsed data or messages to display.
        </Typography>
      </ParsedDataContainer>
    );
  }

  return (
    <ParsedDataContainer>
      <Tabs
        value={activeTabIndex === -1 ? false : activeTabIndex} // MUI Tabs expects false if no tab selected
        onChange={(e, newValue) => setActiveTabIndex(newValue)}
        sx={{
          mb: 2,
          borderBottom: 1,
          borderColor: "divider",
          "& .MuiTab-root": { color: "rgba(255, 255, 255, 0.7)" },
          "& .Mui-selected": { color: "white" },
        }}
      >
        {/* Render tabs dynamically */}
        {tabConfig.map((tab) => (
          <Tab key={tab.key} label={tab.label} />
        ))}
      </Tabs>

      {currentTabKey === "messages" && (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {renderConversation(exampleMessages)}
        </Box>
      )}

      {currentTabKey === "transcript" && parsedData?.transcript?.length > 0 && (
        <>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Typography variant="h6" sx={{ color: "#FF3B30", flex: 1 }}>
              Transcript
            </Typography>
            <IconButton
              onClick={handleEditClick}
              sx={{ color: "white" }}
              disabled={isEditingTranscript}
            >
              <EditIcon />
            </IconButton>
          </Box>

          {isEditingTranscript ? (
            <Box
              sx={{
                mb: 3,
              }}
            >
              <JsonEditor
                multiline
                rows={20}
                value={transcriptJson}
                onChange={(e) => setTranscriptJson(e.target.value)}
                error={Boolean(editError)}
                helperText={editError}
                sx={{
                  mb: 2,
                  flex: 1, // Take up remaining space
                  "& .MuiInputBase-root": {
                    height: "100%", // Make the input field full height
                  },
                  "& .MuiInputBase-input": {
                    height: "100% !important", // Force full height on the textarea
                    overflow: "auto !important", // Enable scrolling
                  },
                }}
              />
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleSaveTranscript}
                  sx={{ flex: 1 }}
                >
                  Save
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setIsEditingTranscript(false)}
                  sx={{ flex: 1 }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          ) : (
            renderTranscript(parsedData.transcript)
          )}
        </>
      )}

      {/* Combined Tab for Dating Profile Details and Observations */}
      {currentTabKey === "imageParsedData" && (
        <Box sx={{ mt: 2 }}>
          {parsedData?.dating_profile_details && (
            <Box sx={{ mb: 3 }}> {/* Add margin bottom if observations will follow */}
              <Typography variant="h6" sx={{ color: "#FFD166", mb: 1 }}>
                Dating Profile Details
              </Typography>
              {/* Using HumanReadableContent for formatting */}
              <HumanReadableContent data={parsedData.dating_profile_details} />
            </Box>
          )}

          {parsedData?.observations && (
            <Box>
              <Typography variant="h6" sx={{ color: "#06D6A0", mb: 1 }}>
                Observations
              </Typography>
              {/* Using HumanReadableContent for formatting */}
              <HumanReadableContent data={parsedData.observations} />
            </Box>
          )}
        </Box>
      )}
    </ParsedDataContainer>
  );
};

export default ParsedDataDisplay;
